/**
 * Wire schemas for admin facility-agreement endpoints (PR #5b).
 *
 * Facilities are immutable once created — there is no update schema.
 * The only mutation after creation is `POST /admin/facilities/:id/activate`.
 *
 * Numeric fields (`brokerageMinPct`, etc.) are transported as strings
 * matching Postgres `numeric` storage; callers should parse with
 * `parseFloat` for display.
 */

import { z } from 'zod';

// ─── Core representation ─────────────────────────────────────────────────────

export const FacilitySchema = z.object({
  id: z.string().uuid(),
  entityId: z.string().uuid(),
  entityName: z.string(),
  version: z.number().int().positive(),

  productScope: z.array(z.string()).min(1),
  brokerageMinPct: z.string(),
  brokerageMaxPct: z.string(),
  canBind: z.boolean(),
  bindLimitB: z.string().nullable(),
  allowedRiskClasses: z.array(z.string()).min(1),
  rateModifiers: z.record(z.string(), z.unknown()),
  discretionaryRangePct: z.string(),

  validFrom: z.string(),
  validTo: z.string(),
  reviewDate: z.string().nullable(),
  isActive: z.boolean(),
  notes: z.string(),
  createdAt: z.string(),
  createdByUserId: z.string().uuid().nullable(),
});

export type Facility = z.infer<typeof FacilitySchema>;

// ─── List ────────────────────────────────────────────────────────────────────

export const FacilityListQuerySchema = z.object({
  entityId: z.string().uuid().optional(),
  isActive: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
});

export type FacilityListQuery = z.infer<typeof FacilityListQuerySchema>;

export const FacilityListResponseSchema = z.object({
  data: z.array(FacilitySchema),
});

export type FacilityListResponse = z.infer<typeof FacilityListResponseSchema>;

// ─── Single ──────────────────────────────────────────────────────────────────

export const FacilityResponseSchema = z.object({
  data: FacilitySchema,
});

export type FacilityResponse = z.infer<typeof FacilityResponseSchema>;

// ─── Create ──────────────────────────────────────────────────────────────────

export const FacilityCreateRequestSchema = z.object({
  entityId: z.string().uuid(),
  productScope: z.array(z.string().min(1)).min(1),
  brokerageMinPct: z.number().min(0).max(100),
  brokerageMaxPct: z.number().min(0).max(100),
  canBind: z.boolean(),
  bindLimitB: z.number().positive().optional().nullable(),
  allowedRiskClasses: z.array(z.string().min(1)).min(1),
  discretionaryRangePct: z.number().min(0).max(100),
  validFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD'),
  validTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD'),
  reviewDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD')
    .optional()
    .nullable(),
  notes: z.string().max(2000).optional().default(''),
});

export type FacilityCreateRequest = z.infer<typeof FacilityCreateRequestSchema>;
